import { HttpClient } from '@angular/common/http';
import { Observable, of, map } from 'rxjs';
import { Injectable } from '@angular/core';
import { Params } from '@angular/router';

import { Occ, OccEndpointsService, Product, ProductImageNormalizer } from '@spartacus/core';

import { AutocompleteResult, GlobalSearchResult, OccGlobalSearchResult, AutocompleteSuggestion, RichAutocompleteResult } from '../models';
import { ConfigurationService } from './configuration.service';
import { ProductMapper } from '../mappers/product.mapper';
import { BaseStoreService } from './base-store.service';

@Injectable({
  providedIn: 'root'
})
export class GlobalSearchService {
  constructor(
    private configurationService: ConfigurationService,
    private http: HttpClient,
    private occEndpointsService: OccEndpointsService,
    private productImageNormalizer: ProductImageNormalizer,
    private baseStoreService: BaseStoreService,
    private productMapper: ProductMapper
  ) {}

  autocompleteSearch(query: string, pageSize?: number, isAccessorySearch?: boolean): Observable<AutocompleteResult> {
    const baseStore = this.baseStoreService.baseStore;

    if (baseStore.enableRichAutocomplete) {
      return this.richAutocompleteSearch(query, pageSize, isAccessorySearch);
    } else {
      return this.simpleAutocompleteSearch(query, isAccessorySearch);
    }
  }

  globalSearch(requestParams: Params): Observable<GlobalSearchResult> {
    const searchQuery = requestParams['query'];
    const sort = requestParams['sortCode'];
    const target = requestParams['target'];
    const currentPage = requestParams['currentPage'];
    const pageSize = requestParams['pageSize'];
    const isAccessorySearch = requestParams['isAccessorySearch'] ? requestParams['isAccessorySearch'] : false;
    const params: any = {
      searchQuery,
      sort,
      currentPage,
      pageSize,
      targets: target || 'product,accessoryProduct,faq,ifu',
      isAccessorySearch
    };
    if (params.searchQuery.length > 2) {
      const url = this.occEndpointsService.buildUrl('globalSearch', { queryParams: params });
      return this.http.get<OccGlobalSearchResult>(url).pipe(
        map((result) => ({
          ...result,
          productSearchPageData: result.productSearchPageData
            ? {
              ...result.productSearchPageData,
              products: result.productSearchPageData?.products?.map((product) =>
                this.productImageNormalizer.convert(product)
              )
            }
            : undefined,
          accessorySearchPageData: result.accessorySearchPageData
            ? {
              ...result.accessorySearchPageData,
              products: result.accessorySearchPageData?.products?.map((product) =>
                this.productImageNormalizer.convert(product)
              )
            }
            : undefined,
          ifuSearchPageData: result.ifuSearchPageData
            ? {
              ...result.ifuSearchPageData,
              products: result.ifuSearchPageData?.products?.map((product) => ({
                ...this.productImageNormalizer.convert(product),
                url: this.productMapper.toIfuUrl(product)
              }))
            }
            : undefined
        }))
      );
    } else {
      return of();
    }
  }

  public simpleAutocompleteSearch(query: string, isAccessorySearch?: boolean): Observable<AutocompleteResult> {
    let params: any = { term: query };
    if (isAccessorySearch !== undefined) {
      params = { ...params, isAccessorySearch };
    }
    const url = this.occEndpointsService.buildUrl('searchAutocomplete', { queryParams: params });
    return this.http.get<{ termList: AutocompleteSuggestion[] }>(url).pipe(
      map(({ termList }) => termList),
      map((result) => ({ rich: false, results: result }))
    );
  }

  private richAutocompleteSearch(
    query: string,
    pageSize?: number,
    isAccessorySearch?: boolean
  ): Observable<AutocompleteResult> {
    const config = this.configurationService.config;
    const paramsCategory = {
      term: query,
      pageSize: String(pageSize || config.autocompleteCategoriesSize)
    };
    let paramsProduct: any = {
      term: query,
      pageSize: String(pageSize || config.autocompleteProductsSize)
    };
    if (isAccessorySearch !== undefined) {
      paramsProduct = { ...paramsProduct, isAccessorySearch };
    }

    const paramsProductAndCategoryResult = {
      ...paramsProduct,
      pageSize: Number(paramsCategory?.pageSize) > Number(paramsProduct?.pageSize)
        ? paramsCategory?.pageSize
        : paramsProduct?.pageSize
    };

    const productAndCategoryUrl = this.occEndpointsService.buildUrl('searchRichAutocomplete', {
      queryParams: paramsProductAndCategoryResult
    });

    return this.http.get<RichAutocompleteResult>(productAndCategoryUrl).pipe(
      map((resultProductAndCategory) => {
        const _resultProduct = resultProductAndCategory.products
          .slice(0, config.autocompleteProductsSize) as Product[] | Occ.Product[];

        const products = _resultProduct?.map((product) =>
          this.productImageNormalizer.convert(product)
        ) ?? [];

        const _resultAccessories = resultProductAndCategory.accessories
          .slice(0, config.autocompleteProductsSize) as Product[] | Occ.Product[];

        const accessories = _resultAccessories?.map((accessory) =>
          this.productImageNormalizer.convert(accessory)
        ) ?? [];

        const categories = resultProductAndCategory.categories
          .slice(0, config.autocompleteCategoriesSize) ?? [];

        return {
          rich: true,
          results: {
            ...resultProductAndCategory,
            products,
            accessories,
            categories
          }
        };
      })
    );
  }
}
